/**
 * Vestra Analytics v1 — the typed event catalog.
 *
 * ONE map from each v1 event name (registered in ANALYTICS_EVENTS) to the
 * exact properties it carries. Web and server emit v1 events through this
 * type (the web `captureV1`, the server `trackV1ServerEvent`), so a missing,
 * misspelled or free-text property is a compile error at the emit site, not a
 * broken PostHog insight a week later.
 *
 * Rules the shapes follow:
 * - Only the properties the v1 plan lists, plus ids already in scope at the
 *   emit site (task / trace / message / interaction / proposal ids).
 * - Enum-like values are closed string unions. Where the codebase already owns
 *   a vocabulary (composer SurfaceId, OD2 trace Surface, ChannelType,
 *   ArtifactKind, HeardAboutChoice, HarnessId) the union reuses it.
 * - Property types are type aliases (not interfaces) so every shape stays
 *   assignable to the `Record<string, unknown>` both SDK wrappers take.
 */

import type { AnalyticsEventName } from './events.js';
import type { Surface } from '../types/agent-trace.js';
import type { SurfaceId } from '../composer/contract.js';
import type { ChannelType } from '../types/channel.js';
import type { ArtifactKind } from '../content-engine/types.js';
import type { AttributionSource, HeardAboutChoice } from '../types/attribution.js';
import type { HarnessId } from '../harness/ids.js';

// ─── shared vocabularies ─────────────────────────────────────────────────────

/** How a person signs in. Supabase's `app_metadata.provider`, folded to what we offer. */
export type AnalyticsAuthProvider = 'google' | 'email' | 'other' | 'unknown';

/** Where a new account came from: AttributionSource['kind'], or 'unknown' when
 *  the signup seam cannot see the attribution yet (it is claimed after). */
export type AnalyticsAcquisitionSource = AttributionSource['kind'] | 'unknown';

/** Whose invite let the account in (invite_codes.scope), 'none' when no code
 *  was used, 'unknown' when the signup seam cannot tell yet. */
export type AnalyticsInviteKind = 'team' | 'partner' | 'user' | 'none' | 'unknown';

/**
 * Where an ask was made. Classic socket sends carry the OD2 trace surface
 * ('web' | 'native-app' | …); asks through the composer gateway carry the
 * composer SurfaceId ('composer' | 'voice_card' | 'telegram' | …). The two
 * vocabularies share no member, so one property never means two things.
 */
export type AnalyticsDelegationSurface = Surface | SurfaceId;

/** How the words of an ask arrived: typed, spoken on a live voice line, or
 *  dictated (the notch's push-to-talk transcript). */
export type AnalyticsInputMode = 'typed' | 'live' | 'dictated';

/** How a delegation's run ended. A cut with no outcome (a WS drop) is not one of these. */
export type AnalyticsDelegationOutcome = 'completed' | 'errored' | 'timed_out' | 'paused' | 'stopped';

/** Which kind of ask the agent (or the home board) put to the person. */
export type AnalyticsInteractionKind = 'permission' | 'question' | 'need';

/** What the person did with it. */
export type AnalyticsInteractionResolution = 'answered' | 'approved' | 'denied';

/**
 * Why a voice session ended, folded from the line's own close reasons:
 * the actor's (hangup, account_blocked, never_started), the vendor's
 * session.closed reason (close_requested, remote_hangup, expired), the
 * transport's (connection_lost), and 'other' for any reason not listed.
 */
export type AnalyticsVoiceEndReason =
  | 'hangup'
  | 'close_requested'
  | 'remote_hangup'
  | 'expired'
  | 'connection_lost'
  | 'account_blocked'
  | 'never_started'
  | 'other';

/** A routine run's real terminal (finalizeRoutineRunByTaskId's flip, or a lost dispatch). */
export type AnalyticsRoutineRunOutcome = 'completed' | 'failed';

/** An onboarding (arrival) screen. The sign-in beat is not onboarding. */
export type AnalyticsOnboardingScreen =
  | 'gate'
  | 'ways'
  | 'plans'
  | 'in'
  | 'q1'
  | 'q2'
  | 'heard'
  | 'connect'
  | 'ready'
  | 'phone'
  | 'memories'
  | 'setup';

/** An onboarding step whose answer the server records. */
export type AnalyticsOnboardingStep = 'heard_about';

/** A sign-up call to action on a marketing page, by placement. */
export type AnalyticsMarketingCtaId = 'nav' | 'nav_mobile' | 'hero' | 'close' | 'footer';

/** The marketing page family a CTA sits on. */
export type AnalyticsMarketingPage = 'vs' | 'use_cases' | 'use_case' | 'marketplace' | 'blog';

/** Where a skill was installed from. */
export type AnalyticsSkillInstallSurface = 'skills_library' | 'skill_detail';

/** Where a creation was opened from. */
export type AnalyticsArtifactOpenSurface = 'home';

/** Where a web client event happened: the browser or the desktop shell's webview. */
export type AnalyticsClientSurface = Extract<Surface, 'web' | 'native-app'>;

/** What a Nami proposal became (nami_proposals.status after resolution). */
export type AnalyticsNamiProposalResolution = 'accepted' | 'accepted_edited' | 'declined';

/** Why a channel reply did not go out: the chat's reply window was closed, or the send failed. */
export type AnalyticsChannelErrorCode = 'outside_window' | 'send_failed';

/**
 * The ONE fold from a durable interaction's `kind` to the catalog's interaction_kind.
 * The server (interaction:requested) and every web surface (interaction:resolved) use
 * it, so an ask and its answer always name the same kind: a question is a question,
 * every other kind asks the person to allow something. Only a need with no backing
 * interaction (no kind to read) is a 'need'.
 */
export function analyticsInteractionKindOf(kind: string | null | undefined): AnalyticsInteractionKind {
  if (!kind) return 'need';
  return kind === 'ask_question' ? 'question' : 'permission';
}

// ─── per-event properties ────────────────────────────────────────────────────

export type UserSignedUpProperties = {
  auth_provider: AnalyticsAuthProvider;
  acquisition_source: AnalyticsAcquisitionSource;
  invite_kind: AnalyticsInviteKind;
};

export type SkillInstalledProperties = {
  skill_slug: string;
  source: 'library';
  surface: AnalyticsSkillInstallSurface;
};

export type IntegrationConnectionConfirmedProperties = {
  /** Composio toolkit slug (an id, not an enum). */
  provider: string;
};

export type HomeArtifactOpenProperties = {
  artifact_kind: ArtifactKind;
  surface: AnalyticsArtifactOpenSurface;
};

export type TaskFirstTokenProperties = {
  task_id: string;
  trace_id: string;
  ttft_ms: number;
  send_path: 'new_task' | 'continuation' | 'teammate';
  first_token_kind: 'text' | 'thinking';
  surface: AnalyticsClientSurface;
  runtime: HarnessId;
  /** The task's pinned model; null when the task carries none (the server picks). */
  model: string | null;
  input_mode: AnalyticsInputMode;
};

export type OnboardingScreenViewedProperties = {
  screen: AnalyticsOnboardingScreen;
};

export type OnboardingStepSubmittedProperties = {
  step: AnalyticsOnboardingStep;
  choice: HeardAboutChoice;
};

export type DelegationSubmittedProperties = {
  /** Absent when the ask went to the composer gateway and no task exists yet. */
  task_id?: string;
  trace_id?: string;
  surface: AnalyticsDelegationSurface;
  input_mode: AnalyticsInputMode;
  message_length: number;
};

export type DelegationAcceptedProperties = {
  task_id: string;
  trace_id?: string;
  surface: AnalyticsDelegationSurface;
  input_mode: AnalyticsInputMode;
};

export type DelegationStartedProperties = {
  task_id: string;
  trace_id: string;
};

export type DelegationFinishedProperties = {
  task_id: string;
  trace_id: string;
  outcome: AnalyticsDelegationOutcome;
  /** Input + output tokens of the run (cache reads excluded). */
  tokens: number;
  tool_count: number;
  duration_ms?: number;
};

export type DelegationResultPresentedProperties = {
  task_id: string;
  message_id?: string;
};

export type InteractionRequestedProperties = {
  interaction_kind: AnalyticsInteractionKind;
  task_id: string;
  interaction_id: string;
};

export type InteractionResolvedProperties = {
  interaction_kind: AnalyticsInteractionKind;
  resolution: AnalyticsInteractionResolution;
  task_id?: string;
  interaction_id?: string;
};

export type VoiceSessionEndedProperties = {
  voice_session_id: string;
  duration_seconds: number;
  billed_seconds: number;
  cost_usd: number;
  end_reason: AnalyticsVoiceEndReason;
  asks_spoken: number;
  asks_typed: number;
};

export type RoutineRunFinishedProperties = {
  routine_id: string;
  outcome: AnalyticsRoutineRunOutcome;
  task_id?: string;
};

export type AuthFlowStartedProperties = {
  auth_provider: AnalyticsAuthProvider;
};

export type MarketingCtaClickedProperties = {
  cta_id: AnalyticsMarketingCtaId;
  page: AnalyticsMarketingPage;
};

export type OnboardingCompletedProperties = Record<string, never>;

export type AbilityFirstUsedProperties = {
  ability_id: string;
};

export type IntegrationConnectStartedProperties = {
  provider: string;
};

export type IntegrationFirstUsedProperties = {
  provider: string;
};

export type CreationCreatedProperties = {
  creation_kind: ArtifactKind;
  task_id: string;
};

export type NamiProposalResolvedProperties = {
  resolution: AnalyticsNamiProposalResolution;
  proposal_id: string;
};

export type ChannelLinkedProperties = {
  channel: ChannelType;
};

export type ChannelResponseSentProperties = {
  channel: ChannelType;
  task_id?: string;
};

export type ChannelResponseFailedProperties = {
  channel: ChannelType;
  error_code: AnalyticsChannelErrorCode;
  task_id?: string;
};

/** Schema only: the properties are decided with the activation definition. */
export type UserActivationAchievedProperties = Record<string, never>;

// ─── the catalog ─────────────────────────────────────────────────────────────

export interface AnalyticsV1EventProperties {
  'user:signed_up': UserSignedUpProperties;
  'skill:installed': SkillInstalledProperties;
  'integration:connection_confirmed': IntegrationConnectionConfirmedProperties;
  'home:artifact_open': HomeArtifactOpenProperties;
  'task:first_token': TaskFirstTokenProperties;
  'onboarding:screen_viewed': OnboardingScreenViewedProperties;
  'onboarding:step_submitted': OnboardingStepSubmittedProperties;
  'delegation:submitted': DelegationSubmittedProperties;
  'delegation:started': DelegationStartedProperties;
  'delegation:finished': DelegationFinishedProperties;
  'interaction:resolved': InteractionResolvedProperties;
  'voice:session_ended': VoiceSessionEndedProperties;
  'routine:run_finished': RoutineRunFinishedProperties;
  'auth:flow_started': AuthFlowStartedProperties;
  'marketing:cta_clicked': MarketingCtaClickedProperties;
  'onboarding:completed': OnboardingCompletedProperties;
  'delegation:accepted': DelegationAcceptedProperties;
  'delegation:result_presented': DelegationResultPresentedProperties;
  'ability:first_used': AbilityFirstUsedProperties;
  'integration:connect_started': IntegrationConnectStartedProperties;
  'integration:first_used': IntegrationFirstUsedProperties;
  'interaction:requested': InteractionRequestedProperties;
  'creation:created': CreationCreatedProperties;
  'nami:proposal_resolved': NamiProposalResolvedProperties;
  'channel:linked': ChannelLinkedProperties;
  'channel:response_sent': ChannelResponseSentProperties;
  'channel:response_failed': ChannelResponseFailedProperties;
  'user:activation_achieved': UserActivationAchievedProperties;
}

export type AnalyticsV1EventName = keyof AnalyticsV1EventProperties;

/** The v1 names at runtime, in plan order (the 27, then the schema-only one). */
export const ANALYTICS_V1_EVENTS = [
  'user:signed_up',
  'skill:installed',
  'integration:connection_confirmed',
  'home:artifact_open',
  'task:first_token',
  'onboarding:screen_viewed',
  'onboarding:step_submitted',
  'delegation:submitted',
  'delegation:started',
  'delegation:finished',
  'interaction:resolved',
  'voice:session_ended',
  'routine:run_finished',
  'auth:flow_started',
  'marketing:cta_clicked',
  'onboarding:completed',
  'delegation:accepted',
  'delegation:result_presented',
  'ability:first_used',
  'integration:connect_started',
  'integration:first_used',
  'interaction:requested',
  'creation:created',
  'nami:proposal_resolved',
  'channel:linked',
  'channel:response_sent',
  'channel:response_failed',
  'user:activation_achieved',
] as const satisfies readonly AnalyticsV1EventName[];

// Compile-time fences: every catalog name is a registered ANALYTICS_EVENTS
// value, and the runtime list names every key of the map.
type Fence<T extends true> = T;
export type _V1NamesAreRegistered = Fence<AnalyticsV1EventName extends AnalyticsEventName ? true : false>;
export type _V1ListIsComplete = Fence<
  Exclude<AnalyticsV1EventName, (typeof ANALYTICS_V1_EVENTS)[number]> extends never ? true : false
>;
